import React, { useState, useCallback } from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { StackScreenProps } from '@react-navigation/stack';
import { BaseAdminScreen, AdminEmptyScreenLayout, AdminSelectDropdown } from '@/components';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useReportReview } from '@/hooks/admin/useReportReview';
import { ReportReviewList } from '@/components/admin/corkBoard/ReportReviewList';
import { ReportDetailModal } from '@/components/admin/corkBoard/ReportDetailModal';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { CORK_BOARD_REPORT_STATUSES } from '@/constants/residents/corkBoardConstants';
import { showConfirm } from '@/utils/common/alert';
import { DesktopReportReviewView } from './desktop/DesktopReportReviewView';
import type { CorkBoardReport, ReportStatus } from '@/types/residents/corkBoard';

type ReportReviewScreenProps = StackScreenProps<AdminStackParamList, 'ReportReview'>;

export const ReportReviewScreen: React.FC<ReportReviewScreenProps> = ({ navigation }) => {
  const { layoutMode } = useResponsive();
  const review = useReportReview();
  const { redesign, semantic } = useAdminColors();

  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedReport, setSelectedReport] = useState<CorkBoardReport | null>(null);

  const handleOpenDetail = useCallback((report: CorkBoardReport) => {
    setSelectedReport(report);
    setDetailVisible(true);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setDetailVisible(false);
    setSelectedReport(null);
  }, []);

  const handleRemove = useCallback(
    async (report: CorkBoardReport) => {
      const confirmed = await showConfirm(
        'Remove Content',
        `Are you sure you want to remove this ${report.contentType}? This will delete the content and resolve the report.`,
        'Remove',
        'Cancel',
      );
      if (confirmed) {
        const success = await review.handleRemoveContent(report);
        if (success) handleCloseDetail();
      }
    },
    [review, handleCloseDetail],
  );

  const handleDismiss = useCallback(
    async (report: CorkBoardReport) => {
      const confirmed = await showConfirm(
        'Dismiss Report',
        'Are you sure you want to dismiss this report? The content will remain visible.',
        'Dismiss',
        'Cancel',
      );
      if (confirmed) {
        const success = await review.handleDismissReport(report.id);
        if (success) handleCloseDetail();
      }
    },
    [review, handleCloseDetail],
  );

  const handleWarn = useCallback(
    async (report: CorkBoardReport) => {
      const confirmed = await showConfirm(
        'Warn User',
        `Issue a warning to the author of this ${report.contentType}? The report will be marked as resolved.`,
        'Warn',
        'Cancel',
      );
      if (confirmed) {
        const success = await review.handleWarnUser(report.id);
        if (success) handleCloseDetail();
      }
    },
    [review, handleCloseDetail],
  );

  const emptyMessage =
    review.statusFilter === 'pending'
      ? 'No pending reports. The community is looking good!'
      : 'No reports match the current filter.';

  if (layoutMode === 'desktop') {
    return (
      <AdminErrorBoundary context="ReportReviewDesktop">
        <DesktopReportReviewView
          review={review}
          detailVisible={detailVisible}
          selectedReport={selectedReport}
          onOpenDetail={handleOpenDetail}
          onCloseDetail={handleCloseDetail}
          onRemove={handleRemove}
          onDismiss={handleDismiss}
          onWarn={handleWarn}
        />
      </AdminErrorBoundary>
    );
  }

  return (
    <AdminErrorBoundary context="ReportReview">
      <BaseAdminScreen
        screenName="ReportReview"
        navigation={navigation}
        showPlaceholder={false}
        scrollEnabled={false}
        contentContainerStyle={styles.baseScreenContent}
      >
        <AdminEmptyScreenLayout
          title="Reports"
          emptyStateType="corkBoardReports"
          showAddButton={false}
          hasContent={true}
          disableScrollView={true}
        >
          <AdminSelectDropdown
            label="Status"
            options={CORK_BOARD_REPORT_STATUSES}
            selectedValue={review.statusFilter}
            onSelect={(value) => review.setStatusFilter(value as ReportStatus | 'all')}
          />

          {review.error && (
            <View
              style={[
                styles.errorBanner,
                {
                  backgroundColor: redesign.accent.redBg,
                  borderBottomColor: semantic.status.error,
                },
              ]}
            >
              <Text style={[styles.errorBannerText, { color: semantic.status.error }]}>
                Failed to load reports.
              </Text>
              <TouchableOpacity onPress={review.refetch}>
                <Text style={[styles.errorRetryText, { color: semantic.status.error }]}>
                  Retry
                </Text>
              </TouchableOpacity>
            </View>
          )}

          <ReportReviewList
            reports={review.reports}
            isLoading={review.isLoading}
            isMutating={review.isMutating}
            onView={handleOpenDetail}
            onRemove={handleRemove}
            onDismiss={handleDismiss}
            onWarn={handleWarn}
            onRefresh={review.refetch}
            isRefreshing={review.isFetching}
            onLoadMore={review.loadMore}
            hasNextPage={review.hasNextPage}
            isFetchingMore={review.isFetching && review.reports.length > 0}
            emptyMessage={emptyMessage}
          />
        </AdminEmptyScreenLayout>

        <ReportDetailModal
          visible={detailVisible}
          report={selectedReport}
          isMutating={review.isMutating}
          onRemove={handleRemove}
          onDismiss={handleDismiss}
          onWarn={handleWarn}
          onClose={handleCloseDetail}
        />

      </BaseAdminScreen>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    flexGrow: 1,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  errorBannerText: {
    fontSize: 13,
  },
  errorRetryText: {
    fontSize: 13,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
});
